import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularMaterialModule } from 'src/app/shared/module/angular-material.module';
import { ApplicationPipesModule } from 'src/app/shared/module/applicationPipesModule';
import { Select } from '@ngxs/store';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Alert } from 'src/app/models/alert';
import { AlertTypes } from 'src/app/models/alertTypes';
import { FeatureSettings, TenantFeatures, isFeatureEnabled } from 'src/app/models/featureSettings';
import { AlertTypesState } from 'src/app/store/state/alert-types.state';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { SpaRoutingService } from 'src/app/shared/services/spa.routing.service';
import { AlertDetailService } from 'src/app/diagnostics/component/alert-detail/alert-detail.service';
import { CaseStatus, CaseStatusService } from 'src/app/diagnostics/component/alert-case/case-status.service';
import { resolveFaultDisplay, findShopGroup } from 'src/app/shared/handler/common-functions.util';
import { AlertSummary } from '../models/service-center-asset-detail';
import { AlertDialogResult } from '../alert-detail-dialog/alert-triage-opener';

export interface AlertQuickViewDialogData {
  alert: AlertSummary;
  vehicleName?: string;
  vin?: string;
}

export const ALERT_QUICK_VIEW_DIALOG_CONFIG: MatDialogConfig = {
  panelClass: 'sc-alert-dialog',
  width: '520px',
  maxWidth: '95vw',
  autoFocus: false,
};

interface FaultLine {
  triggeredAlert: boolean;
  protocol: string;
  faultType: string;
  description: string;
  controller: number;
  controllerDisplay: string;
  faultCode: number;
  faultCodeDisplay: string;
  failureCode: number;
  failureCodeDisplay: string;
  faultCount: string;
  faultDateUtc: Date;
}

@Component({
  selector: 'app-alert-quick-view-dialog',
  templateUrl: './alert-quick-view-dialog.component.html',
  styleUrl: './alert-quick-view-dialog.component.scss',
  standalone: true,
  imports: [CommonModule, AngularMaterialModule, TranslateModule, ApplicationPipesModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertQuickViewDialogComponent implements OnInit, OnDestroy {
  loading = false;
  error = false;
  message: string | null = null;
  faults: FaultLine[] = [];

  caseStatus: CaseStatus | null = null;
  private fullAlert: Alert | null = null;

  get current(): AlertSummary {
    return this.data.alert;
  }

  get caseActionReady(): boolean {
    return !!this.caseStatus && !!this.fullAlert;
  }

  @Select(AlertTypesState.getAlertTypes) getAlertTypes$: Observable<AlertTypes>;
  @SelectSnapshot(TenantSessionState.getTenantFeatures) private tenantFeatures: FeatureSettings;

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AlertQuickViewDialogData,
    private dialogRef: MatDialogRef<AlertQuickViewDialogComponent, AlertDialogResult>,
    private alertDetailService: AlertDetailService,
    private caseStatusService: CaseStatusService,
    private spaRouting: SpaRoutingService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  // Shop name / Over the Road — only known once the full alert (with groups) loads.
  get vehicleLocation(): string | null {
    if (!this.fullAlert) return null;
    const shop = findShopGroup(this.fullAlert.groups);
    return shop ? shop.name : this.translate.instant('@otr');
  }

  get showCase(): boolean {
    return isFeatureEnabled(this.tenantFeatures, TenantFeatures.WorkOrderCreation);
  }

  ngOnInit(): void {
    const alertId = this.data.alert?.latestAlertId;
    if (!alertId) return;

    this.loading = true;
    combineLatest([
      this.alertDetailService.getData(alertId),
      this.getAlertTypes$.pipe(filter(t => !!t?.faultcodes?.length)),
    ]).pipe(
      take(1),
      takeUntil(this.destroy$),
    ).subscribe({
      next: ([alert, alertTypes]) => {
        this.fullAlert = alert;
        this.message = alert.alertRule?.message ?? null;
        this.faults = this.buildFaultLines(alert, alertTypes);
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.error = true;
        this.loading = false;
        this.cdr.markForCheck();
      },
    });

    if (this.showCase) this.loadCase(alertId);
  }

  private loadCase(alertId: string): void {
    this.caseStatusService.caseStatusFor(alertId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.caseStatus = status;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get severityClass(): string {
    return 'sev-' + (this.data.alert?.severity ?? '').toLowerCase();
  }

  viewAlert(): void {
    const alertId = this.data.alert?.latestAlertId;
    if (!alertId) return;
    this.dialogRef.close();
    this.spaRouting.navigateToAlert(alertId);
  }

  // AlertCaseComponent keys off alert.id, which the AlertSummary seed lacks; the
  // full Alert is only available once getData resolves — hence caseActionReady.
  viewCase(): void {
    if (!this.fullAlert) return;
    this.dialogRef.close({ action: 'viewCase', alert: this.fullAlert });
  }

  close(): void {
    this.dialogRef.close();
  }

  private buildFaultLines(alert: Alert, alertTypes: AlertTypes): FaultLine[] {
    const faults = alert.faults ?? [];
    const seen = new Set<string>();
    const lines: FaultLine[] = [];
    for (const f of faults) {
      const key = `${f.protocol}|${f.faultCode}|${f.failureCode}|${f.sourceAddress}|${f.groupId}|${f.faultType}|${f.description}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const display = resolveFaultDisplay(alertTypes, f);
      lines.push({
        triggeredAlert: f.triggeredAlert,
        protocol: f.protocol,
        faultType: f.faultType,
        description: f.description,
        controller: f.sourceAddress,
        controllerDisplay: display.controllerDisplay,
        faultCode: f.faultCode,
        faultCodeDisplay: display.faultCodeDisplay,
        failureCode: f.failureCode,
        failureCodeDisplay: display.failureCodeDisplay,
        faultCount: f.faultCount,
        faultDateUtc: f.faultDateUtc,
      });
    }
    return lines;
  }
}
